import {createColumnHelper} from "@tanstack/react-table";
import i18next from "i18next";
import {useTranslation} from "react-i18next";
import {truncateString} from "../../../Utils/StringHelper";
import {TanStackTable} from "../TanStackTable";
import {SignerPills} from "../../Cards/SignerPills";
import dayjs from "dayjs";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faDownload, faPlus} from "@fortawesome/free-solid-svg-icons";
import {useState, useEffect} from "react";
import {useBoundStore} from "../../../Store/store";

export const PerformanceReportsTable = (props) => {
    const {
        userId,
        branchId,
        forceNewData = false,
        showGenerateButton = false,
        onGenerateClick = null
    } = props;

    const [performanceStatements, setPerformanceStatements] = useState([]);
    const [metaData, setMetaData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const getPerformanceStatementsForUser = useBoundStore((state) => state.getPerformanceStatementsForUser);
    const {t} = useTranslation();

    useEffect(() => {
        const fetchPerformanceStatements = async () => {
            setLoading(true);
            const result = await getPerformanceStatementsForUser(userId, currentPage, branchId);
            setPerformanceStatements(result.data);
            setMetaData(result.meta);
            setLoading(false);
        };

        if (userId || branchId) {
            fetchPerformanceStatements();
        }
    }, [userId, branchId, currentPage, forceNewData, getPerformanceStatementsForUser]);

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('from.formatted', {
            header: t('tables.from'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY'),
        }),
        columnHelper.accessor('freelancer', {
            header: i18next.t('shifts_module.column_freelancer'),
            cell: props => truncateString(`${props.cell.getValue()?.first_name ?? '-'} ${props.cell.getValue()?.last_name ?? '-'}`),
        }),
        columnHelper.accessor('branch', {
            header: t('tables.branch'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('status', {
            header: t('tables.status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        {
                            ['accepted'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.accepted')}</div>
                            )
                        }

                        {
                            ['sent'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-blue-300 text-blue-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_waiting_for_signers')}</div>
                            )
                        }

                        {
                            ['pending'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('general.status.pending')}</div>
                            )
                        }

                        {
                            ['rejected'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('general.status.rejected')}</div>
                            )
                        }
                    </div>
                )
            },
        }),
        columnHelper.accessor('documenso_data', {
            header: t('general.labels.signers'),
            cell: props => <SignerPills recipients={props.cell.getValue()?.recipients} />,
        }),
        columnHelper.accessor('document', {
            header: t('tables.document'),
            cell: props => {
                if((props.cell.getValue() !== null && props.cell.getValue() !== undefined) && props.cell.getValue().hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')}/>
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <Link
                            to={`/admin/performance-reports/${props.row.original.id}`}
                            className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                        >
                            <FontAwesomeIcon icon={faArrowRight} className={'text-sm'}/>
                        </Link>
                    </div>
                )
            },
        })
    ]

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (performanceStatements.length === 0 && !showGenerateButton) {
        return (
            <div className="text-center text-gray-500 py-8">
                {t('performance_reports.no_results')}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {showGenerateButton && onGenerateClick && (
                <div className="flex justify-end">
                    <button
                        onClick={onGenerateClick}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2 text-xs" />
                        {t('performance_reports.generate')}
                    </button>
                </div>
            )}

            {performanceStatements.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                    {t('performance_reports.no_results')}
                </div>
            ) : (
                <TanStackTable
                    columns={columns}
                    tableData={performanceStatements}
                    forceNewData={forceNewData}
                    laravelPaginationMetaData={metaData}
                    onChangePage={(page) => setCurrentPage(page + 1)}
                />
            )}
        </div>
    )
}